import ChatBubble from 'containers/CompositionFeedback/ChatBubble';
import {useState} from 'react';
import caracter from 'assets/images/characters/CarolinaMariadeJesus/pointing-right.webp';
import {Competence, NumericMetadata} from 'utils/types/newReviewed';
import {setReviewGoalService} from 'store/services/compositionServices';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useParams} from 'react-router-dom';
import FeelingsView from 'containers/CompositionFeedback/views/FeelingsView';
import {createUserFeelingsRequest} from 'store/reducers/userFeelings';
import {Button, Modal, Radio} from 'letrus-ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import styles from './GoalModal.module.scss';

interface GoalModalProps {
  id: string;
  reviewId: number;
  onFinish(): void;
  comments: NumericMetadata;
  competences?: Competence[];
  isUserFeelingsOn: boolean;
  createUserFeelingsRequest: typeof createUserFeelingsRequest;
  networkId: number;
  reviewGrid: number;
}

const MATO_GROSSO_SCHOOL_NETWORK_ID = 115;

const MULTIGENRE_REVIEW_GRID_ID = 61;

function GoalModal({
  id,
  onFinish,
  comments,
  reviewId,
  competences,
  createUserFeelingsRequest,
  isUserFeelingsOn,
  networkId,
  reviewGrid,
}: GoalModalProps) {
  const [selectedCompetence, setSelectedCompetence] = useState<number>();
  const [shouldShowUserFeelings, setShouldShowUserFeelings] = useState(false);

  const {composition_id} = useParams();

  const {trackEvent} = useTrackEvent();

  const competencesByPriority = Object.values(comments ?? {}).sort(
    (compA, compB) => {
      const priorityOrder: Record<NumericMetadata['priority'], number> = {
        smaller: 1,
        second_smaller: 2,
        neutral: 3,
        greater: 4,
      };

      const isPriorityEqual =
        priorityOrder[compA.priority] - priorityOrder[compB.priority] === 0;

      if (isPriorityEqual) {
        return compA.score - compB.score;
      }

      return priorityOrder[compA.priority] - priorityOrder[compB.priority];
    },
  );

  const smallerCompetence = competences?.find(
    (competence) => competence.id === competencesByPriority[0]?.competence_id,
  );
  const secondSmallerCompetence = competences?.find(
    (competence) => competence.id === competencesByPriority[1]?.competence_id,
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!selectedCompetence || !reviewId) return;

    trackEvent('step5_feedback_competence_selected', {
      composition: {
        composition_id: Number(composition_id),
      },
      competence: {
        competence_id: selectedCompetence,
      },
    });

    try {
      await setReviewGoalService({
        reviewId,
        data: {competence_id: selectedCompetence},
      });

      if (isUserFeelingsOn) {
        if (networkId === MATO_GROSSO_SCHOOL_NETWORK_ID) {
          if (reviewGrid === MULTIGENRE_REVIEW_GRID_ID) {
            // Multigenre on network 115 → show modal
            setShouldShowUserFeelings(true);
          } else {
            // ENEM on network 115 → does not show modal
            onFinish();
          }
        } else {
          // Other networks → maintains current behavior
          setShouldShowUserFeelings(true);
        }
      } else {
        onFinish();
      }
    } catch (err) {
      // TODO: verify with design and product team what to do in case of error
    }
  }

  return (
    <Modal id={id}>
      {!shouldShowUserFeelings && (
        <div className={styles.container}>
          <h4>Vamos escolher um alvo?</h4>

          <ChatBubble
            imageUrl={caracter}
            variant="character-left"
            message="Para aprimorar ainda mais os seus textos, vamos escolher um ponto para focar na sua próxima redação! O que você escolhe?"
            animationBlocked
            clickBlocked
          />

          <form onSubmit={handleSubmit}>
            <hr />

            <div className={styles.choices}>
              <Radio
                label={`Quero melhorar a ${smallerCompetence?.name}, na qual tive o pior desempenho.`}
                id="firstCompetence"
                name="competence"
                value={smallerCompetence?.id}
                onClick={() => setSelectedCompetence(smallerCompetence?.id)}
              />

              <Radio
                label={`Quero evoluir a ${secondSmallerCompetence?.name}, à qual preciso dedicar mais atenção.`}
                id="secondCompetence"
                name="competence"
                value={secondSmallerCompetence?.id}
                onClick={() =>
                  setSelectedCompetence(secondSmallerCompetence?.id)
                }
              />
            </div>

            <Button disabled={!selectedCompetence || !reviewId}>
              <FontAwesomeIcon icon="bullseye-arrow" />
              Definir alvo
            </Button>
          </form>
        </div>
      )}

      {/* as it is being charged in the contract, the userfeelings feature will temporarily remain within the
goal modal until the final product decision is made */}

      {shouldShowUserFeelings && (
        <FeelingsView
          onFinish={onFinish}
          userFeelingsStage="during-composition review"
          createUserFeelingsRequest={createUserFeelingsRequest}
        />
      )}
    </Modal>
  );
}

export default GoalModal;
